/**
 * Enhanced Cookie Consent Manager - GDPR Compliant
 * Granular controls: Strictly Necessary / Analytics / Marketing
 * Integrates with Google Tag Manager Consent Mode V2
 */
import { useState, useEffect, useRef } from 'react';
import { Cookie, Settings, Shield, BarChart2, Target, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { useLocation } from 'react-router-dom';
import { trackEvent, initAnalytics } from '@/lib/analytics';
import { updateConsent as updateGTMConsent, pushToDataLayer, readSavedConsent, COOKIE_CONSENT_KEY } from '@/lib/gtm';


interface ConsentState {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const translations = {
  en: {
    title: 'Cookie Preferences',
    subtitle: 'We use cookies to improve your experience and analyze site traffic.',
    acceptAll: 'Accept All',
    acceptSelected: 'Save Preferences',
    declineAll: 'Necessary Only',
    customize: 'Customize',
    categories: {
      necessary: {
        title: 'Strictly Necessary',
        description: 'Essential for the website to function. Cannot be disabled.',
      },
      analytics: {
        title: 'Analytics',
        description: 'Help us understand how visitors interact with our website.',
      },
      marketing: {
        title: 'Marketing',
        description: 'Used to deliver personalized advertising.',
      },
    },
    privacyLink: 'Privacy Policy',
    cookieLink: 'Cookie Policy',
  },
  fr: {
    title: 'Préférences de Cookies',
    subtitle: 'Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic.',
    acceptAll: 'Tout Accepter',
    acceptSelected: 'Enregistrer',
    declineAll: 'Nécessaires Uniquement',
    customize: 'Personnaliser',
    categories: {
      necessary: {
        title: 'Strictement Nécessaires',
        description: 'Essentiels au fonctionnement du site. Ne peuvent être désactivés.',
      },
      analytics: {
        title: 'Analytiques',
        description: 'Nous aident à comprendre comment les visiteurs utilisent le site.',
      },
      marketing: {
        title: 'Marketing',
        description: 'Utilisés pour diffuser des publicités personnalisées.',
      },
    },
    privacyLink: 'Politique de Confidentialité',
    cookieLink: 'Politique des Cookies',
  },
  es: {
    title: 'Preferencias de Cookies',
    subtitle: 'Utilizamos cookies para mejorar tu experiencia y analizar el tráfico.',
    acceptAll: 'Aceptar Todo',
    acceptSelected: 'Guardar',
    declineAll: 'Solo Necesarias',
    customize: 'Personalizar',
    categories: {
      necessary: {
        title: 'Estrictamente Necesarias',
        description: 'Esenciales para el funcionamiento del sitio. No se pueden desactivar.',
      },
      analytics: {
        title: 'Analíticas',
        description: 'Nos ayudan a entender cómo los visitantes usan el sitio.',
      },
      marketing: {
        title: 'Marketing',
        description: 'Usadas para mostrar publicidad personalizada.',
      },
    },
    privacyLink: 'Política de Privacidad',
    cookieLink: 'Política de Cookies',
  },
  ar: {
    title: 'تفضيلات ملفات تعريف الارتباط',
    subtitle: 'نستخدم ملفات تعريف الارتباط لتحسين تجربتك وتحليل حركة المرور.',
    acceptAll: 'قبول الكل',
    acceptSelected: 'حفظ التفضيلات',
    declineAll: 'الضرورية فقط',
    customize: 'تخصيص',
    categories: {
      necessary: {
        title: 'ضرورية تمامًا',
        description: 'أساسية لعمل الموقع. لا يمكن تعطيلها.',
      },
      analytics: {
        title: 'تحليلية',
        description: 'تساعدنا على فهم كيفية تفاعل الزوار مع موقعنا.',
      },
      marketing: {
        title: 'تسويقية',
        description: 'تُستخدم لتقديم إعلانات مخصصة.',
      },
    },
    privacyLink: 'سياسة الخصوصية',
    cookieLink: 'سياسة ملفات تعريف الارتباط',
  },
  de: {
    title: 'Cookie-Einstellungen', subtitle: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und den Verkehr zu analysieren.',
    acceptAll: 'Alle Akzeptieren', acceptSelected: 'Speichern', declineAll: 'Nur Notwendige', customize: 'Anpassen',
    categories: { necessary: { title: 'Unbedingt Erforderlich', description: 'Essentiell für die Funktion der Website.' }, analytics: { title: 'Analytik', description: 'Helfen uns zu verstehen, wie Besucher die Website nutzen.' }, marketing: { title: 'Marketing', description: 'Werden für personalisierte Werbung verwendet.' } },
    privacyLink: 'Datenschutzrichtlinie', cookieLink: 'Cookie-Richtlinie',
  },
  it: {
    title: 'Preferenze Cookie', subtitle: 'Utilizziamo i cookie per migliorare la tua esperienza e analizzare il traffico.',
    acceptAll: 'Accetta Tutto', acceptSelected: 'Salva', declineAll: 'Solo Necessari', customize: 'Personalizza',
    categories: { necessary: { title: 'Strettamente Necessari', description: 'Essenziali per il funzionamento del sito.' }, analytics: { title: 'Analitici', description: 'Ci aiutano a capire come i visitatori usano il sito.' }, marketing: { title: 'Marketing', description: 'Utilizzati per pubblicità personalizzata.' } },
    privacyLink: 'Informativa Privacy', cookieLink: 'Informativa Cookie',
  },
  pt: {
    title: 'Preferências de Cookies', subtitle: 'Usamos cookies para melhorar sua experiência e analisar o tráfego.',
    acceptAll: 'Aceitar Todos', acceptSelected: 'Salvar', declineAll: 'Apenas Necessários', customize: 'Personalizar',
    categories: { necessary: { title: 'Estritamente Necessários', description: 'Essenciais para o funcionamento do site.' }, analytics: { title: 'Analíticos', description: 'Nos ajudam a entender como os visitantes usam o site.' }, marketing: { title: 'Marketing', description: 'Usados para publicidade personalizada.' } },
    privacyLink: 'Política de Privacidade', cookieLink: 'Política de Cookies',
  },
  nl: {
    title: 'Cookie Voorkeuren', subtitle: 'Wij gebruiken cookies om uw ervaring te verbeteren en verkeer te analyseren.',
    acceptAll: 'Alles Accepteren', acceptSelected: 'Opslaan', declineAll: 'Alleen Noodzakelijk', customize: 'Aanpassen',
    categories: { necessary: { title: 'Strikt Noodzakelijk', description: 'Essentieel voor de werking van de website.' }, analytics: { title: 'Analytisch', description: 'Helpen ons begrijpen hoe bezoekers de site gebruiken.' }, marketing: { title: 'Marketing', description: 'Gebruikt voor gepersonaliseerde advertenties.' } },
    privacyLink: 'Privacybeleid', cookieLink: 'Cookiebeleid',
  },
  pl: {
    title: 'Preferencje Cookies', subtitle: 'Używamy plików cookie, aby poprawić Twoje wrażenia i analizować ruch.',
    acceptAll: 'Zaakceptuj Wszystkie', acceptSelected: 'Zapisz', declineAll: 'Tylko Niezbędne', customize: 'Dostosuj',
    categories: { necessary: { title: 'Ściśle Niezbędne', description: 'Niezbędne do działania strony.' }, analytics: { title: 'Analityczne', description: 'Pomagają zrozumieć, jak odwiedzający korzystają ze strony.' }, marketing: { title: 'Marketingowe', description: 'Używane do wyświetlania spersonalizowanych reklam.' } },
    privacyLink: 'Polityka Prywatności', cookieLink: 'Polityka Cookies',
  },
  ru: {
    title: 'Настройки Cookie', subtitle: 'Мы используем cookie для улучшения вашего опыта и анализа трафика.',
    acceptAll: 'Принять Все', acceptSelected: 'Сохранить', declineAll: 'Только Необходимые', customize: 'Настроить',
    categories: { necessary: { title: 'Строго Необходимые', description: 'Необходимы для работы сайта.' }, analytics: { title: 'Аналитические', description: 'Помогают понять, как посетители используют сайт.' }, marketing: { title: 'Маркетинговые', description: 'Используются для персонализированной рекламы.' } },
    privacyLink: 'Политика Конфиденциальности', cookieLink: 'Политика Cookie',
  },
  zh: {
    title: 'Cookie偏好设置', subtitle: '我们使用Cookie来改善您的体验并分析网站流量。',
    acceptAll: '全部接受', acceptSelected: '保存', declineAll: '仅必要的', customize: '自定义',
    categories: { necessary: { title: '严格必要', description: '网站正常运行所必需。' }, analytics: { title: '分析', description: '帮助我们了解访客如何使用网站。' }, marketing: { title: '营销', description: '用于投放个性化广告。' } },
    privacyLink: '隐私政策', cookieLink: 'Cookie政策',
  },
  ja: {
    title: 'Cookie設定', subtitle: 'お客様の体験向上とサイトトラフィック分析のためにCookieを使用しています。',
    acceptAll: 'すべて承認', acceptSelected: '保存', declineAll: '必要なもののみ', customize: 'カスタマイズ',
    categories: { necessary: { title: '必須', description: 'ウェブサイトの機能に必須です。' }, analytics: { title: '分析', description: '訪問者のサイト利用状況を理解するのに役立ちます。' }, marketing: { title: 'マーケティング', description: 'パーソナライズ広告の配信に使用されます。' } },
    privacyLink: 'プライバシーポリシー', cookieLink: 'Cookieポリシー',
  },
  tr: {
    title: 'Çerez Tercihleri', subtitle: 'Deneyiminizi geliştirmek ve site trafiğini analiz etmek için çerez kullanıyoruz.',
    acceptAll: 'Tümünü Kabul Et', acceptSelected: 'Kaydet', declineAll: 'Sadece Gerekli', customize: 'Özelleştir',
    categories: { necessary: { title: 'Kesinlikle Gerekli', description: 'Web sitesinin çalışması için zorunludur.' }, analytics: { title: 'Analitik', description: 'Ziyaretçilerin siteyi nasıl kullandığını anlamamıza yardımcı olur.' }, marketing: { title: 'Pazarlama', description: 'Kişiselleştirilmiş reklamlar için kullanılır.' } },
    privacyLink: 'Gizlilik Politikası', cookieLink: 'Çerez Politikası',
  },
  ko: {
    title: '쿠키 환경설정', subtitle: '경험 개선 및 사이트 트래픽 분석을 위해 쿠키를 사용합니다.',
    acceptAll: '모두 수락', acceptSelected: '저장', declineAll: '필수만', customize: '사용자 정의',
    categories: { necessary: { title: '필수', description: '웹사이트 작동에 필수적입니다.' }, analytics: { title: '분석', description: '방문자가 사이트를 어떻게 사용하는지 이해하는 데 도움이 됩니다.' }, marketing: { title: '마케팅', description: '맞춤형 광고 제공에 사용됩니다.' } },
    privacyLink: '개인정보 처리방침', cookieLink: '쿠키 정책',
  },
  sv: {
    title: 'Cookie-inställningar', subtitle: 'Vi använder cookies för att förbättra din upplevelse och analysera trafik.',
    acceptAll: 'Acceptera Alla', acceptSelected: 'Spara', declineAll: 'Endast Nödvändiga', customize: 'Anpassa',
    categories: { necessary: { title: 'Strikt Nödvändiga', description: 'Väsentliga för webbplatsens funktion.' }, analytics: { title: 'Analytiska', description: 'Hjälper oss förstå hur besökare använder webbplatsen.' }, marketing: { title: 'Marknadsföring', description: 'Används för personaliserad annonsering.' } },
    privacyLink: 'Integritetspolicy', cookieLink: 'Cookie-policy',
  },
  da: {
    title: 'Cookie-præferencer', subtitle: 'Vi bruger cookies for at forbedre din oplevelse og analysere trafik.',
    acceptAll: 'Accepter Alle', acceptSelected: 'Gem', declineAll: 'Kun Nødvendige', customize: 'Tilpas',
    categories: { necessary: { title: 'Strengt Nødvendige', description: 'Essentielle for websitens funktion.' }, analytics: { title: 'Analytiske', description: 'Hjælper os med at forstå, hvordan besøgende bruger websiten.' }, marketing: { title: 'Marketing', description: 'Bruges til at levere personaliseret annoncering.' } },
    privacyLink: 'Privatlivspolitik', cookieLink: 'Cookie-politik',
  },
};

export function CookieConsentManager() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    necessary: true,
    analytics: false,
    marketing: false,
  });
  const { language: ctxLanguage, isRTL: ctxIsRTL } = useLanguage();
  // Mounted outside the /:lang LanguageProvider, so read the language from the URL first.
  const { pathname } = useLocation();
  const urlLang = pathname.split('/')[1] || '';
  const language = urlLang in translations ? urlLang : ctxLanguage;
  const isRTL = urlLang in translations ? urlLang === 'ar' : ctxIsRTL;

  const t = translations[language as keyof typeof translations] || translations.en;

  // No implicit consent: until the visitor chooses, only strictly necessary storage is granted.
  const defaultConsent: ConsentState = { necessary: true, analytics: false, marketing: false };

  const applyConsent = (consentState: ConsentState) => {
    // Update GTM Consent Mode V2
    updateGTMConsent(consentState);
    
    // Push consent state to dataLayer for debugging
    pushToDataLayer('consent_applied', {
      consent_necessary: consentState.necessary,
      consent_analytics: consentState.analytics,
      consent_marketing: consentState.marketing,
    });
  };

  useEffect(() => {
    const saved = readSavedConsent();
    if (saved) {
      setConsent(saved);
      applyConsent(saved);
      return;
    }
    setConsent(defaultConsent);
    applyConsent(defaultConsent);
    const timer = setTimeout(() => setIsVisible(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const saveConsent = (consentState: ConsentState) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentState));
    } catch (err) {
      console.warn('[consent] could not persist choice; applied for this page only', err);
    }
    setConsent(consentState);
    applyConsent(consentState);
    if (consentState.analytics) initAnalytics();
    setIsVisible(false);
    trackEvent('cookie_consent_saved', { consent: consentState });
  };

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: true });
  };

  const declineAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
  };

  const acceptSelected = () => {
    saveConsent(consent);
  };

  const panelRef = useRef<HTMLDivElement>(null);
  // Publish the banner height so floating widgets (chat/WhatsApp) can sit above it.
  useEffect(() => {
    const root = document.documentElement;
    if (!isVisible || !panelRef.current) {
      root.removeAttribute('data-cmp-open');
      root.style.removeProperty('--cmp-h');
      return;
    }
    const el = panelRef.current;
    const update = () => root.style.setProperty('--cmp-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    update();
    root.setAttribute('data-cmp-open', '');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => { ro?.disconnect(); root.removeAttribute('data-cmp-open'); root.style.removeProperty('--cmp-h'); };
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div
      ref={panelRef}
      data-cmp
      role="region"
      aria-labelledby="cmp-title"
      aria-describedby="cmp-desc"
      className="fixed bottom-0 inset-x-0 z-[60] p-2 sm:p-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto max-w-4xl px-0 sm:px-4">
        <div className="bg-background border border-border rounded-xl sm:rounded-2xl p-2.5 sm:p-5 shadow-2xl max-h-[45vh] sm:max-h-none overflow-y-auto">
          <div className="flex items-start gap-3">
            <Cookie className="hidden sm:block w-6 h-6 text-primary flex-shrink-0 mt-0.5" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <h2 id="cmp-title" className="sr-only sm:not-sr-only sm:text-base font-semibold text-foreground">{t.title}</h2>
              <p id="cmp-desc" className="text-xs leading-snug sm:text-sm text-muted-foreground">
                {t.subtitle}{' '}
                <a href={`/${language}/legal/cookies`} className="underline hover:text-primary">{t.cookieLink}</a>
                <span className="hidden sm:inline"> · <a href={`/${language}/legal/privacy`} className="underline hover:text-primary">{t.privacyLink}</a></span>
              </p>
            </div>
          </div>

          {showDetails && (
            <div id="cmp-details" className="space-y-3 mt-3 pt-3 border-t border-border">
              {([
                ['necessary', Shield],
                ['analytics', BarChart2],
                ['marketing', Target],
              ] as const).map(([key, Icon]) => (
                <div key={key} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4 text-primary flex-shrink-0" aria-hidden="true" />
                    <div className="min-w-0">
                      <p id={`cmp-${key}`} className="text-sm font-medium text-foreground">{t.categories[key].title}</p>
                      <p className="text-xs text-muted-foreground">{t.categories[key].description}</p>
                    </div>
                  </div>
                  <Switch
                    aria-labelledby={`cmp-${key}`}
                    checked={key === 'necessary' ? true : consent[key]}
                    disabled={key === 'necessary'}
                    onCheckedChange={key === 'necessary' ? undefined : (checked) => setConsent({ ...consent, [key]: checked })}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-[1fr_1fr_auto] sm:flex sm:items-center sm:justify-end gap-2 mt-2 sm:mt-3">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowDetails(!showDetails)}
              aria-expanded={showDetails}
              aria-controls="cmp-details"
              className="order-last sm:order-first sm:me-auto h-9 px-2 text-xs text-muted-foreground hover:text-foreground gap-1"
            >
              <Settings className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="sr-only sm:not-sr-only">{t.customize}</span>
              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showDetails ? 'rotate-180' : ''}`} aria-hidden="true" />
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={declineAll} className="h-9 px-2 text-xs sm:text-sm whitespace-normal leading-tight">
              {t.declineAll}
            </Button>
            {showDetails ? (
              <Button type="button" size="sm" onClick={acceptSelected} className="h-9 px-2 text-xs sm:text-sm whitespace-normal leading-tight">
                {t.acceptSelected}
              </Button>
            ) : (
              <Button type="button" size="sm" onClick={acceptAll} className="h-9 px-2 text-xs sm:text-sm whitespace-normal leading-tight">
                {t.acceptAll}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
